import {
  Component,
  Prop,
  Host,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  h,
  State,
  Event,
  EventEmitter,
  Method,
  Listen,
  Element,
} from '@stencil/core';
import {
  AllyConfig,
  ModalStyle,
  HostMerchantMessage,
} from './addi-product-modal.d';
import { HostMerchantMessageType } from './addi-product-modal.const';
import { LoanProprosal } from '../addi-product-widget/addi-product-widget.d';
import { closeBtnImg } from '../../assets/icons';
import {
  formatCurrency,
  getBrazilEmbeddedBaseURL,
  getEmbeddedBaseURL,
  getLocationOrigin,
  getOriginationsBaseURL,
  getPreapprovalURL,
} from '../../utils/utils';
import { EMBEDDED_ONBOARDING_WIDGET_PATH } from '../../constants';
import { HEALTH_CHECK_TYPE } from '../../constants/health-check';

const PRODUCT_DETAILS_WIDGET_MODAL_CLOSED_EVENT =
  'PRODUCT_DETAILS_WIDGET_MODAL_CLOSED';
@Component({
  tag: 'addi-product-modal',
  styleUrl: 'addi-product-modal.scss',
  shadow: true,
})
export class AddiProductModal {
  @Prop() showPreapproval: boolean;
  @Prop() redirectPreapproval: boolean;
  @Prop() customModalStyle: ModalStyle;
  @Event() healthCheckValidate: EventEmitter<HEALTH_CHECK_TYPE>;

  @Prop() proposal: LoanProprosal;
  @Prop() allyConfig: AllyConfig;
  @Prop() country: string;

  @Prop() preapprovalVersion: string;

  @State() isOpen = false;
  @State() iframeLoaded = false;

  @Element() el: HTMLElement;

  language = 'co';
  iframeElement = null;

  @Method()
  async close() {
    this.isOpen = false;
  }

  @Method()
  async open() {
    this.isOpen = true;
    this.healthCheckValidate.emit(HEALTH_CHECK_TYPE.modal);
  }

  @Listen('message', { target: 'window' })
  hideModalThroughMessage(event: MessageEvent) {
    if (event.data === 'close-modal') {
      this.close();
    }
  }

  @Listen('message', { target: 'window' })
  handleMessage(Event) {
    this.messagesListener(Event);
  }

  componentWillLoad() {
    this.language = this.country === 'co' ? 'co' : 'pt';
  }

  componentDidLoad() {
    this.iframeElement = this.el.shadowRoot.querySelector('.addi-modal_iframe');
  }

  handleCloseModalClick(event: UIEvent) {
    const RELOADABLE_URLS = ['localhost', 'preapproval'];
    event.preventDefault();
    window.parent &&
      window.parent.postMessage(PRODUCT_DETAILS_WIDGET_MODAL_CLOSED_EVENT, '*');
    this.close();
    if (
      RELOADABLE_URLS.some((url) => this.iframeElement.src.indexOf(url) > -1)
    ) {
      const url = this.iframeElement.src;
      this.iframeElement.src = url;
    }
  }

  setIframeSrc() {
    if (this.country === 'co') {
      if (this.redirectPreapproval || this.preapprovalVersion === 'v2') {
        return `${getPreapprovalURL(this.allyConfig.allySlug)}?allySlug=${
          this.allyConfig.allySlug
        }&amount=${this.proposal.requestedAmount}`;
      }

      return `${getEmbeddedBaseURL(
        this.allyConfig.allySlug
      )}${EMBEDDED_ONBOARDING_WIDGET_PATH}?${this.getParameters()}`;
    } else {
      return `${getBrazilEmbeddedBaseURL()}${EMBEDDED_ONBOARDING_WIDGET_PATH}?${this.getParameters()}`;
    }
  }

  getParameters() {
    if (this.redirectPreapproval && this.country === 'br') {
      return `allySlug=${
        this.allyConfig.allySlug
      }${''}&allyDomain=${getLocationOrigin()}${''}&preapprovalLink=${
        this.showPreapproval ? this.showPreapproval : ''
      }${''}&redirectPreapproval=${
        this.showPreapproval ? this.redirectPreapproval : ''
      }${''}&language=pt`;
    }

    const { clientLoanSimulated, prospectLoanSimulated } = this.proposal || {};
    const hasDiscount = !!clientLoanSimulated || !!prospectLoanSimulated;
    let requestedAmountWithDiscount,
      minTerm,
      maxTerm,
      totalInstallment,
      kindClientDiscount = '';
    const showPreapprovalLink = this.showPreapproval;
    const isZeroInterest = this.proposal?.effectiveAnnualRate === 0 ? true : '';
    const isUnderMinAmount =
      this.proposal?.requestedAmount < this.allyConfig?.minAmount;
    const minAmount = this.allyConfig?.minAmount
      ? formatCurrency(+this.allyConfig.minAmount)
      : '';

    if (hasDiscount) {
      kindClientDiscount = clientLoanSimulated ? 'client' : 'prospect';
      requestedAmountWithDiscount = clientLoanSimulated
        ? clientLoanSimulated?.requestedAmountWithDiscount
        : prospectLoanSimulated?.requestedAmountWithDiscount;
      totalInstallment = clientLoanSimulated
        ? formatCurrency(clientLoanSimulated?.totalInstallment)
        : formatCurrency(prospectLoanSimulated?.totalInstallment);
      minTerm = clientLoanSimulated
        ? clientLoanSimulated?.minTerm || ''
        : prospectLoanSimulated?.minTerm || '';
      maxTerm = clientLoanSimulated
        ? clientLoanSimulated?.maxTerm || ''
        : prospectLoanSimulated?.maxTerm || '';
    } else {
      totalInstallment = this.proposal?.totalInstallment;
    }

    return `productType=${this.allyConfig?.policy?.productType}${''}&term=${
      this.proposal?.term
    }${''}&totalInstallment=${totalInstallment}${''}&effectiveAnnualRate=${
      this.proposal?.effectiveAnnualRate
    }${''}&totalGuaranteeRate=${
      this.proposal?.totalGuaranteeRate
    }${''}&requestedAmount=${formatCurrency(
      +this.proposal?.requestedAmount
    )}${''}&requestedAmountWithDiscount=${formatCurrency(
      +requestedAmountWithDiscount
    )}${''}&minTerm=${minTerm}${''}&maxTerm=${maxTerm}${''}&hasDiscount=${
      hasDiscount ? hasDiscount : ''
    }${''}&kindClientDiscount=${kindClientDiscount}${''}&allySlug=${
      this.allyConfig.allySlug
    }${''}&allyDomain=${getLocationOrigin()}${''}&preapprovalLink=${
      showPreapprovalLink ? showPreapprovalLink : ''
    }${''}&isZeroInterest=${isZeroInterest}${''}&isUnderMinAmount=${
      isUnderMinAmount ? 'true' : ''
    }${''}&minAmount=${minAmount}${''}&language=${
      this.language
    }${''}&template=${
      this.allyConfig?.widgetConfig?.widgetVersion
    }${''}&modalStyle=${
      this.customModalStyle
        ? encodeURIComponent(JSON.stringify(this.customModalStyle))
        : ''
    }`;
  }

  /**
   * It start to listen 'message' events that are intended to came from the checkout
   * application inside the iframe and handles the communication with the iframe
   */
  messagesListener = (event: MessageEvent) => {
    const message: HostMerchantMessage = event.data;
    //TODO: Convert this in a Switch Case
    if (message.type === HostMerchantMessageType.WidgetGetHostOriginRequest) {
      this.getHostOrigin();
      return;
    }
    if (message.type === HostMerchantMessageType.WidgetCloseModalRequest) {
      this.close();
      return;
    }
  };

  getHostOrigin() {
    const hostOrigin = window?.location?.origin;

    const response: HostMerchantMessage<{ hostOrigin: string }> = {
      type: HostMerchantMessageType.WidgetGetHostOriginResponse,
      payload: {
        hostOrigin,
      },
    };

    this.iframeElement.contentWindow.postMessage(response, '*');
  }

  render() {
    return (
      <Host>
        <div
          class={`addi-modal_overlay ${
            this.isOpen
              ? 'addi-modal_overlay--open'
              : 'addi-modal_overlay--closed'
          }`}
          onClick={(e) => this.handleCloseModalClick(e)}
        >
          <div
            class={`addi-modal addi-modal${
              this.showPreapproval ? '--wide' : '--normal'
            }`}
            style={{
              'background-color':
                this.customModalStyle?.backgroundColor || 'white',
            }}
          >
            <div
              class="addi-modal_header"
              onKeyDown={(evt) => {
                if (evt.key === 'Escape' || evt.key === 'Esc') {
                  this.handleCloseModalClick(evt);
                }
              }}
            >
              <button onClick={(e) => this.handleCloseModalClick(e)}>
                <img
                  class="addi-close-button"
                  src={closeBtnImg}
                  width={24}
                  height={24}
                />
              </button>
            </div>
            <div class={`addi-loading${this.iframeLoaded ? '--removed' : ''}`}>
              <iframe
                class="addi-modal_iframe"
                onLoad={() => {
                  this.iframeLoaded = true;
                }}
                allow={`camera ${getOriginationsBaseURL()}`}
                src={this.setIframeSrc()}
              ></iframe>
            </div>
          </div>
        </div>
      </Host>
    );
  }
}
